import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import { useSelector } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import FontFamily from '../../constants/fonts';
import { AdminColors } from '../Admin/constants/adminColors';
import PTDropDown from './PTDropDown';
import LhTextInput from '../../components/LhTextInput';
import LhButton from '../../components/LhButton';
import { useToast } from '../../components/LhToastContainer';
import LhDatePicker from '../../components/LhDatePicker';
import { EquipmentValue, RouteTypeItem, DeliveryListItem } from './types';
import {
  fetchActiveRouteTypes,
  fetchActiveLoadSequences,
  fetchEquipmentConfigItems,
  fetchPhotoCategories,
  fetchRouteNumbersByLocation,
  findCertificationForRoute,
  createCheckRecord,
  createStandaloneCheckRecord,
  submitCheckerVerification,
} from './perfectTruckApi';
import { EquipmentConfigItem, PhotoCategoryItem } from '../Admin/pages/PerfectTruckManagement/types';
import PTPhotoUpload from './PTPhotoUpload';
import type { RootState } from '../../store';

// Convert Date -> yyyy-MM-dd for the API
const toApiDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Determine default delivery date based on current local time (mirrors PerfectTruckCreate)
const getDefaultDeliveryDate = (): Date => {
  const now = new Date();
  const hours = now.getHours();
  if (hours < 4) {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
};

const normalizeEquipmentRouteTypes = (routeType: EquipmentConfigItem['routeType']): string[] => {
  if (Array.isArray(routeType)) {
    return routeType.map(v => String(v).trim()).filter(Boolean);
  }
  if (!routeType) return [];
  return String(routeType).split(',').map(v => v.trim()).filter(Boolean);
};

const normalizePhotoRouteTypes = (routeType: PhotoCategoryItem['routeType']): string[] => {
  if (Array.isArray(routeType)) {
    return routeType.map(v => String(v).trim()).filter(Boolean);
  }
  if (!routeType) return [];
  return String(routeType).split(',').map(v => v.trim()).filter(Boolean);
};

const isAllRouteType = (routeTypes: string[]): boolean => {
  if (routeTypes.length === 0) return true;
  return routeTypes.some(rt => rt.toLowerCase() === 'all');
};

const filterEquipmentByRouteType = (
  items: EquipmentConfigItem[],
  selectedRouteType?: string,
): EquipmentConfigItem[] => {
  if (!selectedRouteType) return items;
  const selected = selectedRouteType.trim().toLowerCase();
  return items.filter(item => {
    const configuredRoutes = normalizeEquipmentRouteTypes(item.routeType);
    if (isAllRouteType(configuredRoutes)) return true;
    return configuredRoutes.some(rt => rt.toLowerCase() === selected);
  });
};

const filterPhotoCategoriesByRouteType = (
  items: PhotoCategoryItem[],
  selectedRouteType?: string,
): PhotoCategoryItem[] => {
  if (!selectedRouteType) return items;
  const selected = selectedRouteType.trim().toLowerCase();
  return items.filter(item => {
    const configured = normalizePhotoRouteTypes((item as any).routeType);
    if (isAllRouteType(configured)) return true;
    return configured.some(rt => rt.toLowerCase() === selected);
  });
};

// ── Sub-components ────────────────────────────────────────────────────────────

interface CheckRowProps {
  item: EquipmentConfigItem;
  value: EquipmentValue;
  onChange: (id: number, val: EquipmentValue) => void;
  isMobile?: boolean;
  alt?: boolean;
  hasDetails?: boolean;
}

const CheckRow: React.FC<CheckRowProps> = ({ item, value, onChange, isMobile, alt, hasDetails }) => (
  <View style={[
    styles.equipmentRow,
    isMobile && styles.equipmentRowMobile,
    isMobile && alt && styles.equipmentRowMobileAlt,
    hasDetails && { borderBottomWidth: 0 },
  ]}>
    <Text style={styles.equipmentLabel}>{item.equipmentName}</Text>
    <View style={styles.equipmentOpts}>
      <TouchableOpacity
        style={[styles.eqOption, value === 'yes' && styles.eqOptionYes]}
        onPress={() => onChange(item.id, value === 'yes' ? null : 'yes')}
      >
        <Text style={[styles.eqOptionText, value === 'yes' && styles.eqOptionTextSelected]}>
          {item.checkerPosLabel}
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.eqOption, value === 'no' && styles.eqOptionNo]}
        onPress={() => onChange(item.id, value === 'no' ? null : 'no')}
      >
        <Text style={[styles.eqOptionText, value === 'no' && styles.eqOptionTextSelected]}>
          {item.checkerNegLabel}
        </Text>
      </TouchableOpacity>
    </View>
  </View>
);

// ── Main component ────────────────────────────────────────────────────────────

export interface PerfectTruckCheckCreateHandle {
  handleNavigationAttempt: (callback: () => void) => void;
}

interface PerfectTruckCheckCreateProps {
  onCancel: () => void;
  onSubmit: () => void;
}

const PerfectTruckCheckCreate = forwardRef<PerfectTruckCheckCreateHandle, PerfectTruckCheckCreateProps>(({ onCancel, onSubmit }, ref) => {
  const toast = useToast();
  const selectedLocation = useSelector((state: RootState) => state.appData?.selectedLocation);
  const isMobile = Platform.OS !== 'web';

  // Route Information fields (same as Create Certification)
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(null);
  const [routeType, setRouteType] = useState<any>(null);
  const [routeNumber, setRouteNumber] = useState('');
  const [loadSequence, setLoadSequence] = useState<any>({ id: 'A', displayText: 'A' });
  const [asset, setAsset] = useState('');
  const [assetError, setAssetError] = useState('');

  // Config data
  const [routeTypes, setRouteTypes] = useState<RouteTypeItem[]>([]);
  const [loadSequenceOptions, setLoadSequenceOptions] = useState<{ id: string; displayText: string }[]>([]);
  const [allCheckItems, setAllCheckItems] = useState<EquipmentConfigItem[]>([]);
  const [allPhotoCategories, setAllPhotoCategories] = useState<PhotoCategoryItem[]>([]);
  const [routeNumbers, setRouteNumbers] = useState<string[]>([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  // Check Q&A form state
  const [checkEquipment, setCheckEquipment] = useState<Record<number, EquipmentValue>>({});
  const [checkRemarks, setCheckRemarks] = useState<Record<number, string>>({});
  const [truckOrganized, setTruckOrganized] = useState<EquipmentValue>(null);
  const [checkComments, setCheckComments] = useState('');

  // Photos
  const [imageFileNames, setImageFileNames] = useState<Record<string, string>>({});
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [photoCardWidth, setPhotoCardWidth] = useState(0);
  const CARD_PADDING = 40;
  const MAX_VISIBLE = 3;

  // Existing certification lookup (merge case per acceptance criteria)
  const [existingCertification, setExistingCertification] = useState<DeliveryListItem | null>(null);
  const [isCheckingExisting, setIsCheckingExisting] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [routeError, setRouteError] = useState('');
  const [apiError, setApiError] = useState('');

  const isWhdRoute = useMemo(() => {
    if (!routeType?.id) return false;
    return routeTypes.find(rt => String(rt.id) === routeType.id || rt.routeType === routeType.id)?.deliveryType === 'WHD';
  }, [routeTypes, routeType?.id]);

  const checkItems = useMemo(
    () => filterEquipmentByRouteType(allCheckItems, routeType?.id),
    [allCheckItems, routeType?.id],
  );
  const photoCategories = useMemo(
    () => filterPhotoCategoriesByRouteType(allPhotoCategories, routeType?.id),
    [allPhotoCategories, routeType?.id],
  );

  const loadConfig = useCallback(async () => {
    try {
      setIsLoadingConfig(true);
      const [rtData, eqData, pcData] = await Promise.all([
        fetchActiveRouteTypes(),
        fetchEquipmentConfigItems(),
        fetchPhotoCategories(),
      ]);
      setRouteTypes(rtData);
      setAllCheckItems(
        eqData.filter(i => i.showOnCheckerForm && i.isActive).sort((a, b) => a.displayOrder - b.displayOrder),
      );
      setAllPhotoCategories(
        (pcData || []).filter(p => p.isActive).sort((a, b) => a.displayOrder - b.displayOrder),
      );

      let lsOptions: { id: string; displayText: string }[] = [];
      try {
        const lsData = await fetchActiveLoadSequences();
        if (lsData && lsData.length > 0) {
          lsOptions = [...lsData]
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(ls => ({ id: ls.loadSequence, displayText: ls.loadSequence }));
        }
      } catch {
        // fallback applied below
      }
      if (lsOptions.length === 0) {
        lsOptions = [
          { id: 'A', displayText: 'A' },
          { id: 'B', displayText: 'B' },
          { id: 'C', displayText: 'C' },
        ];
      }
      setLoadSequenceOptions(lsOptions);
      if (lsOptions.length > 0) setLoadSequence(lsOptions[0]);

      if (selectedLocation?.name) {
        try {
          const routeNums = await fetchRouteNumbersByLocation(selectedLocation.name);
          setRouteNumbers(routeNums);
        } catch {
          setRouteNumbers([]);
        }
      }

      const defaultDate = getDefaultDeliveryDate();
      setDeliveryDate(defaultDate);
    } catch (e: any) {
      setApiError(e?.response?.data?.message ?? e?.message ?? 'Failed to load configuration.');
    } finally {
      setIsLoadingConfig(false);
    }
  }, [selectedLocation?.name]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const routeTypeOptions = routeTypes.map(rt => ({ id: rt.routeType, displayText: rt.routeType }));
  const routeNumberOptions = routeNumbers.map(rn => ({ id: rn, displayText: rn }));

  const handlePhotoUploaded = (label: string, data: { fileName: string; imageUrl: string }) => {
    setImageFileNames(prev => ({ ...prev, [label]: data.fileName }));
    setImageUrls(prev => ({ ...prev, [label]: data.imageUrl }));
  };

  const handleEquipmentChange = (id: number, val: EquipmentValue) => {
    setCheckEquipment(prev => ({ ...prev, [id]: val }));
    if (val !== 'yes') {
      setCheckRemarks(prev => {
        const c = { ...prev };
        delete c[id];
        return c;
      });
    }
  };
  const handleCheckRemarksChange = (id: number, text: string) => {
    setCheckRemarks(prev => ({ ...prev, [id]: text }));
  };

  const handleAssetChange = (value: string) => {
    if (value.length > 15) {
      setAssetError('Asset ID cannot exceed 15 characters');
    } else {
      setAsset(value);
      setAssetError('');
    }
  };

  // Looks up whether a certification already exists for this route/date so the
  // check can be merged into it (per acceptance criteria) instead of created standalone.
  const checkExistingCertification = useCallback(async (
    rNum: string,
    lSeq: string | undefined,
    dDate: Date | null,
  ) => {
    const effectiveLSeq = isWhdRoute ? undefined : lSeq;
    if (!rNum.trim() || (!isWhdRoute && !lSeq) || !dDate) {
      setExistingCertification(null);
      setRouteError('');
      return;
    }
    const apiDate = toApiDate(dDate);
    try {
      setIsCheckingExisting(true);
      setRouteError('');
      const match = await findCertificationForRoute(rNum.trim(), effectiveLSeq, apiDate, selectedLocation?.name);
      setExistingCertification(match);
    } catch {
      setExistingCertification(null);
    } finally {
      setIsCheckingExisting(false);
    }
  }, [selectedLocation?.name, isWhdRoute]);

  const handleDeliveryDateChange = (date: Date | null) => {
    setDeliveryDate(date);
    checkExistingCertification(routeNumber, loadSequence?.id, date);
  };
  const handleRouteTypeChange = (value: any) => {
    setRouteType(value);
  };
  const handleLoadSequenceChange = (value: any) => {
    setLoadSequence(value);
    checkExistingCertification(routeNumber, value?.id, deliveryDate);
  };
  const handleRouteNumberChange = (selectedOption: any) => {
    const val = selectedOption?.id || '';
    setRouteNumber(val);
    checkExistingCertification(val, loadSequence?.id, deliveryDate);
  };
  const handleRouteNumberTextChange = (text: string) => {
    setRouteNumber(text);
    checkExistingCertification(text, loadSequence?.id, deliveryDate);
  };

  const handleNavigationAttempt = (callback: () => void) => {
    callback();
  };
  useImperativeHandle(ref, () => ({ handleNavigationAttempt }));

  const handleComplete = async (action: 'approve' | 'reject') => {
    setApiError('');

    if (!deliveryDate) { setApiError('Delivery Date is required.'); return; }
    if (!routeType) { setApiError('Route Type is required.'); return; }
    if (!routeNumber.trim()) { setApiError('Route Number is required.'); return; }
    if (!isWhdRoute && !asset.trim()) { setApiError('Asset is required.'); return; }
    if (!isWhdRoute && !loadSequence?.id) { setApiError('Load Sequence is required.'); return; }
    if (!selectedLocation?.id || !selectedLocation?.name) {
      setApiError('No location selected. Please select a location before submitting.');
      return;
    }

    // Block check record creation if an approved certification already exists
    if (existingCertification && existingCertification.status === 'Approved') {
      setApiError(`A check record for the same delivery date and route that already has an approved certification status cannot be created. Please select a different delivery date or route.`);
      return;
    }

    // When a certification already exists, both approve and reject merge into it.
    const isMerge = !!existingCertification;
    const finalAction: 'approve' | 'reject' = action;

    if (finalAction === 'approve') {
      const unanswered = checkItems.filter(item => !checkEquipment[item.id]);
      if (unanswered.length > 0) {
        setApiError(`Please select a value for: ${unanswered.map(i => i.equipmentName).join(', ')}.`);
        return;
      }
      if (!truckOrganized) {
        setApiError('Please select a value for Truck Organized.');
        return;
      }
      const missingRemarks = checkItems.filter(
        item => item.isTextBoxRequired && checkEquipment[item.id] === 'yes' && !(checkRemarks[item.id] && checkRemarks[item.id].trim()),
      );
      if (missingRemarks.length > 0) {
        setApiError(`Please provide details for: ${missingRemarks.map(i => i.equipmentName).join(', ')}.`);
        return;
      }
    } else if (!checkComments.trim()) {
      setApiError('Comments are required when rejecting.');
      return;
    }

    const checkerResponse = JSON.stringify(
      Object.fromEntries(
        checkItems.map(item => {
          const val = checkEquipment[item.id];
          const label = val === 'yes' ? item.checkerPosLabel : val === 'no' ? item.checkerNegLabel : null;
          const remarks = (checkRemarks as Record<number, string>)[item.id]?.trim();
          if (label == null) return [item.equipmentName, null];
          if (item.isTextBoxRequired || (remarks && remarks.length > 0)) {
            return [item.equipmentName, { value: label, remarks: remarks ?? '' }];
          }
          return [item.equipmentName, label];
        }),
      ),
    );
    const truckOrganizedValue = truckOrganized === 'yes' ? 'Yes' : truckOrganized === 'no' ? 'No' : undefined;
    const checkerImageResponse = Object.keys(imageFileNames).length > 0 ? JSON.stringify(imageFileNames) : undefined;

    try {
      setIsSubmitting(true);

      if (isMerge && existingCertification) {
        await submitCheckerVerification(existingCertification.id, {
          checkerResponse,
          truckOrganized: truckOrganizedValue,
          checkerComments: checkComments || undefined,
          checkerImageResponse,
          status: finalAction === 'approve' ? 'Approved' : 'Rejected',
        });
        const statusLabel = finalAction === 'approve' ? 'Approved' : 'Rejected';
        toast.showToast({
          header: 'Check Added to Certification',
          subtext: `This check has been added to the existing certification for route ${routeNumber} and marked ${statusLabel}.`,
          variant: 'success',
        });
      } else {
        // No existing certification: create a standalone check record
        await createStandaloneCheckRecord({
          routeNumber: routeNumber.trim(),
          routeType: routeType.id,
          ...(loadSequence && !isWhdRoute && { loadSeq: loadSequence.id || undefined }),
          ...(asset.trim() && { assetId: asset.trim() }),
          deliveryDate: toApiDate(deliveryDate),
          locationId: selectedLocation.id,
          locationName: selectedLocation.name,
          checkerResponse,
          truckOrganized: truckOrganizedValue,
          checkerComments: checkComments || undefined,
          checkerImageResponse,
          status: finalAction === 'approve' ? 'Approved - No Certification' : 'Rejected - No Certification',
        });
        toast.showToast({
          header: finalAction === 'approve' ? 'Check Approved' : 'Check Rejected',
          subtext: 'Your check record has been submitted successfully.',
          variant: 'success',
        });
      }
      onSubmit();
    } catch (e: any) {
      setApiError(e?.response?.data?.message ?? e?.message ?? 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingConfig) {
    return (
      <View style={styles.loadingWrapper}>
        <ActivityIndicator size="large" color={AdminColors.blue700} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={[styles.contentContainer, isMobile && styles.contentContainerMobile]}>
      {/* Route Information */}
      <View style={[styles.card, isMobile && styles.cardMobile]}>
        <View style={styles.formTitle}>
          <Text style={styles.formTitleText}>Route Information</Text>
        </View>
        <View style={[styles.formGrid, isMobile && styles.formGridMobile, { marginTop: 16 }]}>
          <View style={[styles.field, isMobile && styles.fieldMobile]}>
            <Text style={styles.fieldLabel}>
              <Text style={styles.req}>* </Text>
              {isWhdRoute ? 'Expected Delivery Date' : 'Delivery Date'}
            </Text>
            <LhDatePicker value={deliveryDate} onChange={handleDeliveryDateChange} placeholder="Select date" />
          </View>
          <View style={[styles.field, isMobile && styles.fieldMobile]}>
            <Text style={styles.fieldLabel}><Text style={styles.req}>* </Text>Route Type</Text>
            <PTDropDown
              childList={routeTypeOptions}
              selectedValue={routeType}
              setSelectedValue={handleRouteTypeChange}
              placeholder="Select"
              label=""
            />
          </View>
          <View style={[styles.field, isMobile && styles.fieldMobile]}>
            <Text style={styles.fieldLabel}>
              <Text style={styles.req}>* </Text>
              {isWhdRoute ? 'BOL#' : 'Route Number'}
            </Text>
            {isWhdRoute ? (
              <LhTextInput value={routeNumber} onChangeText={handleRouteNumberTextChange} placeholder="Enter BOL#" />
            ) : routeNumbers.length > 0 ? (
              <PTDropDown
                childList={routeNumberOptions}
                selectedValue={routeNumber ? { id: routeNumber, displayText: routeNumber } : null}
                setSelectedValue={handleRouteNumberChange}
                placeholder="Select"
                label=""
              />
            ) : (
              <>
                <LhTextInput value={routeNumber} onChangeText={handleRouteNumberTextChange} placeholder="Enter route number" />
                <Text style={styles.noRoutesNote}>No routes found for this location. Enter manually.</Text>
              </>
            )}
            {isCheckingExisting ? <Text style={styles.noRoutesNote}>Checking for existing certification…</Text> : null}
            {routeError ? <Text style={styles.fieldError}>{routeError}</Text> : null}
          </View>
          {!isWhdRoute && (
            <View style={styles.twoColRow}>
              <View style={[styles.field, styles.fieldTwoCol]}>
                <Text style={styles.fieldLabel}><Text style={styles.req}>* </Text>Load Sequence</Text>
                <PTDropDown
                  childList={loadSequenceOptions}
                  selectedValue={loadSequence}
                  setSelectedValue={handleLoadSequenceChange}
                  placeholder="Select"
                  label=""
                />
              </View>
              <View style={[styles.field, styles.fieldTwoCol]}>
                <Text style={styles.fieldLabel}><Text style={styles.req}>* </Text>Asset</Text>
                <LhTextInput value={asset} onChangeText={handleAssetChange} placeholder="T-0000" />
                {assetError ? <Text style={styles.fieldError}>{assetError}</Text> : null}
              </View>
            </View>
          )}
          {isWhdRoute && (
            <View style={[styles.field, isMobile && styles.fieldMobile]}>
              <Text style={styles.fieldLabel}>Asset <Text style={styles.optionalLabel}>optional</Text></Text>
              <LhTextInput value={asset} onChangeText={handleAssetChange} placeholder="T-0000" />
            </View>
          )}
        </View>

        {existingCertification ? (
          <View style={[
            styles.mergeBanner,
            existingCertification.status === 'Approved' && { backgroundColor: AdminColors.red75, borderLeftColor: AdminColors.red400 },
          ]}>
            <MaterialIcons
              name={existingCertification.status === 'Approved' ? 'error-outline' : 'info-outline'}
              size={16}
              color={existingCertification.status === 'Approved' ? AdminColors.red400 : AdminColors.blue700}
            />
            <Text style={[
              styles.mergeBannerText,
              existingCertification.status === 'Approved' && { color: AdminColors.red400 },
            ]}>
              {existingCertification.status === 'Approved'
                ? 'A certification already exists for this route and delivery date with Approved status. You cannot create a check record for this route and delivery date.'
                : `A certification already exists for this route and delivery date. Approving this check will add it to that certification and mark it Approved. Rejecting this check will add it to that certification and mark it Rejected.`}
            </Text>
          </View>
        ) : null}
      </View>

      {/* Equipment Photos */}
      <View style={[styles.card, isMobile && styles.cardMobile]} onLayout={e => setPhotoCardWidth(e.nativeEvent.layout.width)}>
        <View style={styles.formTitle}>
          <Text style={styles.formTitleText}>Equipment Photos</Text>
        </View>
        {isMobile ? (
          <View style={styles.photoRowMobile}>
            {photoCategories.length > 0 ? (
              photoCategories.map((category) => {
                const innerWidth = photoCardWidth - CARD_PADDING;
                const PHOTO_COLS = 3;
                const PHOTO_GAP_MOBILE = 8;
                const totalGaps = (PHOTO_COLS - 1) * PHOTO_GAP_MOBILE;
                const mobileBoxWidth = innerWidth > 0 ? Math.floor((innerWidth - totalGaps) / PHOTO_COLS) : undefined;
                return (
                  <PTPhotoUpload
                    key={category.id}
                    label={category.photoLabel}
                    aspectRatio={1}
                    boxWidth={mobileBoxWidth}
                    initialImageUrl={imageUrls[category.photoLabel]}
                    onUploaded={(data) => handlePhotoUploaded(category.photoLabel, data)}
                  />
                );
              })
            ) : (
              <Text style={styles.noteText}>No photo categories configured.</Text>
            )}
          </View>
        ) : (
          <View
            style={[
              styles.photoRowDesktop,
              photoCategories.length > MAX_VISIBLE && Platform.OS === 'web' && { display: 'flex' as any, flexWrap: 'wrap' as any },
            ]}
          >
            {photoCategories.length > 0 ? (
              photoCategories.map((category) => (
                <View
                  key={`${category.id}-photo-wrapper`}
                  style={photoCategories.length > MAX_VISIBLE ? { width: '30%' as any, flexGrow: 0, flexShrink: 0 } : { flex: 1 }}
                >
                  <PTPhotoUpload
                    key={`${category.id}-${imageFileNames[category.photoLabel] ?? 'empty'}`}
                    label={category.photoLabel}
                    aspectRatio={16 / 10}
                    initialImageUrl={imageUrls[category.photoLabel]}
                    onUploaded={(data) => handlePhotoUploaded(category.photoLabel, data)}
                  />
                </View>
              ))
            ) : (
              <Text style={styles.noteText}>No photo categories configured.</Text>
            )}
          </View>
        )}
      </View>

      {/* Check Questions */}
      <View style={[styles.card, isMobile && styles.cardMobile]}>
        <View style={styles.formTitle}>
          <Text style={styles.formTitleText}>Check Questions</Text>
        </View>
        <View style={{ marginTop: 12 }}>
          <View style={[styles.systemRow, isMobile && styles.systemRowMobile]}>
            <View style={styles.systemRowLabel}>
              <Text style={styles.equipmentLabel}>Truck Organized</Text>
            </View>
            <View style={styles.equipmentOpts}>
              <TouchableOpacity
                style={[styles.eqOption, truckOrganized === 'yes' && styles.eqOptionYes]}
                onPress={() => setTruckOrganized(truckOrganized === 'yes' ? null : 'yes')}
              >
                <Text style={[styles.eqOptionText, truckOrganized === 'yes' && styles.eqOptionTextSelected]}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.eqOption, truckOrganized === 'no' && styles.eqOptionNo]}
                onPress={() => setTruckOrganized(truckOrganized === 'no' ? null : 'no')}
              >
                <Text style={[styles.eqOptionText, truckOrganized === 'no' && styles.eqOptionTextSelected]}>No</Text>
              </TouchableOpacity>
            </View>
          </View>

          {checkItems.map((item, idx) => (
            <View key={item.id}>
              <CheckRow
                item={item}
                value={checkEquipment[item.id] ?? null}
                onChange={handleEquipmentChange}
                isMobile={isMobile}
                alt={idx % 2 === 1}
                hasDetails={item.isTextBoxRequired && checkEquipment[item.id] === 'yes'}
              />
              {item.isTextBoxRequired && checkEquipment[item.id] === 'yes' ? (
                <View style={styles.commentsFieldInline}>
                  <Text style={styles.detailsLabel}>Additional Details</Text>
                  <LhTextInput
                    value={checkRemarks[item.id] ?? ''}
                    onChangeText={t => handleCheckRemarksChange(item.id, t)}
                    placeholder="Enter details"
                    multiline
                    numberOfLines={3}
                    style={styles.multilineInput}
                  />
                </View>
              ) : null}
            </View>
          ))}
          {checkItems.length === 0 && <Text style={styles.noteText}>No check items configured.</Text>}

          <View style={styles.commentsField}>
            <Text style={styles.fieldLabel}>Comments</Text>
            <TextInput
              style={styles.commentsInput}
              multiline
              numberOfLines={3}
              placeholder="Notes about this check..."
              placeholderTextColor={AdminColors.textMuted}
              value={checkComments}
              onChangeText={setCheckComments}
            />
          </View>
        </View>
      </View>

      {/* Actions */}
      {apiError ? (
        <View style={styles.validationError}>
          <MaterialIcons name="error-outline" size={16} color={AdminColors.red400} />
          <Text style={styles.validationErrorText}>{apiError}</Text>
        </View>
      ) : null}
      <View style={[styles.formActions, isMobile && styles.formActionsMobile]}>
        <LhButton
          text="Cancel"
          variant="secondary"
          size={isMobile ? 'small' : 'medium'}
          style={isMobile ? styles.actionBtnMobile : undefined}
          textStyle={isMobile ? styles.actionBtnTextMobile : undefined}
          onPress={onCancel}
        />
        <LhButton
          text={isSubmitting ? 'Submitting…' : 'Reject'}
          variant="secondary"
          size={isMobile ? 'small' : 'medium'}
          style={isMobile ? styles.actionBtnMobile : undefined}
          textStyle={isMobile ? styles.actionBtnTextMobile : undefined}
          onPress={() => handleComplete('reject')}
          disabled={isSubmitting || (existingCertification?.status === 'Approved')}
        />
        <LhButton
          text={isSubmitting ? 'Submitting…' : 'Approve'}
          variant="primary"
          size={isMobile ? 'small' : 'medium'}
          style={isMobile ? styles.actionBtnMobile : undefined}
          textStyle={isMobile ? styles.actionBtnTextMobile : undefined}
          onPress={() => handleComplete('approve')}
          disabled={isSubmitting || (existingCertification?.status === 'Approved')}
        />
      </View>
    </ScrollView>
  );
});

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: AdminColors.bgPage },
  contentContainer: { padding: 20 },
  contentContainerMobile: { padding: 14, paddingBottom: 32 },
  loadingWrapper: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: 48 },
  card: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 10,
    padding: 20,
    marginBottom: 20,
    ...Platform.select({
      web: { boxShadow: '0 0 24px rgba(0,94,192,0.08)' } as any,
      default: { elevation: 2 },
    }),
  },
  cardMobile: {
    backgroundColor: 'transparent',
    borderRadius: 0,
    padding: 0,
    marginBottom: 20,
    ...Platform.select({
      web: { boxShadow: 'none' } as any,
      default: { elevation: 0 },
    }),
  },
  formTitle: {
    borderBottomWidth: 2,
    borderBottomColor: AdminColors.borderTable,
    paddingBottom: 8,
    marginBottom: 16,
  },
  formTitleText: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    color: AdminColors.blue700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  formGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 14 },
  formGridMobile: { flexDirection: 'column', flexWrap: 'nowrap', gap: 14, alignItems: 'stretch' },
  field: { flex: 1, minWidth: 160, flexDirection: 'column', gap: 4 },
  fieldMobile: { flex: 0, flexBasis: 'auto', width: '100%', alignSelf: 'stretch', minWidth: 0 },
  twoColRow: { flexDirection: 'row', gap: 10, alignItems: 'flex-start' },
  fieldTwoCol: { flex: 1, flexBasis: 0, width: 'auto', alignSelf: 'auto', minWidth: 0 },
  fieldLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    color: AdminColors.textPrimary,
    marginBottom: 4,
  },
  optionalLabel: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textMuted,
    fontStyle: 'italic',
  },
  commentsFieldInline: { marginTop: 0, marginBottom: 8 },
  detailsLabel: {
    fontFamily: FontFamily.medium,
    fontSize: 11,
    color: AdminColors.textMuted,
    marginBottom: 6,
  },
  multilineInput: { minHeight: 64, textAlignVertical: 'top' },
  fieldError: {
    fontFamily: FontFamily.medium,
    fontSize: 11,
    color: AdminColors.red400,
    marginTop: 2,
  },
  noRoutesNote: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textMuted,
    fontStyle: 'italic',
    marginTop: 4,
  },
  req: { color: AdminColors.red400 },
  noteText: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textMuted,
    fontStyle: 'italic',
    marginTop: 8,
    marginBottom: 10,
  },
  mergeBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: AdminColors.blue25,
    borderLeftWidth: 3,
    borderLeftColor: AdminColors.blue700,
    borderRadius: 8,
    padding: 12,
    marginTop: 16,
  },
  mergeBannerText: {
    flex: 1,
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.blue700,
    lineHeight: 17,
  },
  photoRowDesktop: { flexDirection: 'row', gap: 12, marginTop: 8, paddingBottom: 4 },
  photoRowMobile: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  equipmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray50,
  },
  equipmentRowMobile: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    borderBottomWidth: 1.5,
    borderBottomColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
    marginBottom: 6,
  },
  equipmentRowMobileAlt: { backgroundColor: AdminColors.blue25 },
  equipmentLabel: { fontFamily: FontFamily.bold, fontSize: 13, color: AdminColors.textPrimary, flex: 1 },
  equipmentOpts: { flexDirection: 'row', gap: 6 },
  eqOption: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
  },
  eqOptionYes: { backgroundColor: AdminColors.green600, borderColor: AdminColors.green600 },
  eqOptionNo: { backgroundColor: AdminColors.red400, borderColor: AdminColors.red400 },
  eqOptionText: { fontFamily: FontFamily.bold, fontSize: 12, color: AdminColors.textSecondary },
  eqOptionTextSelected: { color: AdminColors.textOnDark },
  systemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray50,
  },
  systemRowMobile: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: AdminColors.borderTable,
    borderBottomWidth: 1.5,
    borderBottomColor: AdminColors.borderTable,
    backgroundColor: AdminColors.blue25,
    marginBottom: 6,
  },
  systemRowLabel: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  formActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    paddingTop: 8,
    marginBottom: 20,
  },
  formActionsMobile: {
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    gap: 10,
    paddingVertical: 8,
  },
  actionBtnMobile: { flex: 1, height: 44, borderRadius: 22, paddingHorizontal: 6, justifyContent: 'center' },
  actionBtnTextMobile: { fontSize: 13 },
  validationError: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: AdminColors.red75,
    borderLeftWidth: 3,
    borderLeftColor: AdminColors.red400,
    borderRadius: 6,
    padding: 12,
    marginBottom: 12,
  },
  validationErrorText: {
    flex: 1,
    fontFamily: FontFamily.medium,
    fontSize: 13,
    color: AdminColors.red400,
    lineHeight: 18,
  },
  commentsField: { marginTop: 14 },
  commentsInput: {
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    padding: 12,
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: AdminColors.textPrimary,
    minHeight: 80,
    textAlignVertical: 'top',
  },
});

export default PerfectTruckCheckCreate;
